import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search, ChevronRight } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Message } from "@/types";
import { ModelSuggestionCard } from "@/features/chat/components/renderers/ModelSuggestionCard";
import { useCreativeGenerationStore } from "@/features/creative-generation";
import { HarmonySparkles } from "@/components/icons";
import { cn } from "@/lib/utils";
import { useChatAccessStore } from "@/features/chat/store/chat-access-store";
import { useCurrentChatId } from "@/hooks";

interface MessageModelSuggestionsProps {
  message: Message;
  className?: string; // Optional custom className (merged with defaults)
}

/**
 * MessageModelSuggestions
 * Mesaj içindeki model suggestion'ları render eder
 * Kendi logic'ini kendi içinde yönetir (self-contained)
 *
 * Not: En fazla 3 model gösterildiği için (bkz. modelsToShow) burada gerçek
 * bir carousel'e gerek yok — sabit sayıda kart her zaman responsive bir
 * grid'e sığıyor. Eskiden scroll/index/resize-listener tabanlı bir carousel
 * vardı; desktop'ta (3 kart = 3 itemsPerView) ok butonları zaten hiç
 * tetiklenmiyordu, yani sadece gereksiz state ve karmaşıklıktı.
 */
export const MessageModelSuggestions: React.FC<
  MessageModelSuggestionsProps
> = ({ message, className }) => {
  const { t } = useTranslation();
  const { openFromAssistant } = useCreativeGenerationStore();

  // 🔒 Shared read-only mod kontrolü
  const currentChatId = useCurrentChatId();
  const chatAccess = useChatAccessStore((state) =>
    currentChatId ? state.byChatId[currentChatId] : undefined
  );
  const isReadOnly = chatAccess?.canEdit === false;

  const modelsToShow = useMemo(
    () => message.modelSuggestions?.slice(0, 3) ?? [],
    [message.modelSuggestions]
  );

  // ===== Discover More Handler =====
  const handleDiscoverMore = useCallback(() => {
    // Shared read-only modda wizard akışını tetiklemeyelim
    if (isReadOnly) return;

    if (!message.modelSuggestions || message.modelSuggestions.length === 0) {
      return;
    }

    // MessageId'yi wizard tarafında prompt'u auto-fill için kullan
    const rawMessageId = message.id;
    const normalizedMessageId = rawMessageId?.replace(/_(assistant|user)$/, "");

    // ModelSuggestion'ları EachalabModel'e dönüştür
    const convertedModels = message.modelSuggestions.map((model) => ({
      ...model,
      _id: model._id,
      // 📌 Wizard navigate akışında kullanılmak üzere messageId'yi modele enjekte et
      __messageId: normalizedMessageId,
      brand: model.brand
        ? {
            _id: model.brand._id,
            title: model.brand.title,
            _v: (model.brand as any)._v || 0,
            thumbnail: model.brand.thumbnail || {
              type: undefined,
              size: undefined,
              folder: undefined,
              fileUrl: undefined,
            },
          }
        : {
            _id: "",
            title: "",
            _v: 0,
            thumbnail: {
              type: undefined,
              size: undefined,
              folder: undefined,
              fileUrl: undefined,
            },
          },
    }));

    // openFromAssistant ile AI Model Seçici modalını aç
    openFromAssistant({
      type: "CREATIVE_GENERATION",
      models: convertedModels as any,
      // Gelecekte openWizardFromAssistantPayload için de kullanılabilmesi adına context'i sakla
      messageId: normalizedMessageId,
      userMessageText: (message as any).content || undefined,
    });
  }, [isReadOnly, message.modelSuggestions, openFromAssistant]);

  // Model suggestions yoksa render etme (hook'lardan sonra kontrol)
  if (
    message.role !== "assistant" ||
    !message.modelSuggestions ||
    message.modelSuggestions.length === 0
  ) {
    return null;
  }

  return (
    <div className={cn("mt-4 w-full min-w-0", className)}>
      {/* Model Suggestion Info Message */}
      <div className="flex items-center gap-2 text-text-tertiary dark:text-text-tertiary italic mb-4">
        <HarmonySparkles
          size={16}
          className="text-text-tertiary dark:text-text-tertiary opacity-60"
        />
        <span className="text-sm opacity-70">
          {t("chat.modelSuggestions.generationStarted")}
        </span>
      </div>

      <Tooltip.Provider
        delayDuration={200}
        skipDelayDuration={100}
        disableHoverableContent={false}
      >
        <div className="flex flex-col gap-3 w-full">
          {/* Sabit sayıda (en fazla 3) kart: responsive grid yeterli, carousel gerekmiyor */}
          <div className="grid grid-cols-1 @min-[520px]/chat:grid-cols-2 @min-[880px]/chat:grid-cols-3 gap-3 @min-[520px]/chat:gap-4">
            {modelsToShow.map((model, index) => (
              <ModelSuggestionCard
                key={model._id || `model-${index}`}
                model={
                  {
                    id: model._id,
                    ...model,
                  } as any
                }
                messageId={message.id}
              />
            ))}
          </div>

          {/* Discover More Button */}
          {message.modelSuggestions && message.modelSuggestions.length > 3 && (
            <div className="px-1">
              <button
                onClick={handleDiscoverMore}
                className="bg-bg-primary dark:bg-gray-900 border border-border-secondary dark:border-gray-700 rounded-lg px-3 py-2 sm:px-3.5 sm:py-2.5 flex items-center gap-2 hover:border-border-primary dark:hover:border-gray-600 transition-all duration-200"
              >
                <Search
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-text-secondary dark:text-gray-400"
                  strokeWidth={2}
                />
                <span className="text-xs sm:text-sm font-semibold text-text-secondary dark:text-gray-300">
                  {t("chat.modelSuggestions.discoverMore")}
                </span>
                <ChevronRight
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-text-secondary dark:text-gray-400"
                  strokeWidth={2}
                />
              </button>
            </div>
          )}
        </div>
      </Tooltip.Provider>
    </div>
  );
};
